// eventText.ts — pure text/date helpers shared by the build-calendar edge
// function, the portal's Schedule build window (which previews the exact
// homeowner text) and the portal's unit tests (src/lib/buildCalendarText.test.ts).
// No Deno-only or browser-only APIs in here: keep it importable from both.
//
// The copy under bardin-portal/supabase/functions/build-calendar/ must stay
// byte-identical to the one next to the deployed function
// (CRM Claude/supabase/functions/build-calendar/).

export type ReasonKey = "weather" | "crew" | "materials" | "homeowner_request" | "other";

export const REASON_KEYS: ReasonKey[] = ["weather", "crew", "materials", "homeowner_request", "other"];

/** Labels on the button's reason select (Greg, Oct 16, 2026). */
export const REASON_LABELS: Record<ReasonKey, string> = {
  weather: "Weather",
  crew: "Crew can't make it today",
  materials: "Materials",
  homeowner_request: "Homeowner request",
  other: "Other",
};

export function isReasonKey(v: unknown): v is ReasonKey {
  return typeof v === "string" && (REASON_KEYS as string[]).includes(v);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function isDateOnly(v: unknown): v is string {
  return typeof v === "string" && DATE_ONLY.test(v);
}

/** Midday anchor so a date-only string never slips a day (the May 9 trap). */
function noon(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T12:00:00`);
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDaysISO(iso: string, days: number): string {
  const d = noon(iso);
  d.setDate(d.getDate() + days);
  return toISO(d);
}

export const dayBeforeISO = (iso: string) => addDaysISO(iso, -1);
export const dayAfterISO = (iso: string) => addDaysISO(iso, 1);

export function isWeekendISO(iso: string): boolean {
  const dow = noon(iso).getDay();
  return dow === 0 || dow === 6;
}

/** SRS Marietta delivers Monday–Friday only (Greg, Oct 16, 2026), so the
 *  default material drop is the last weekday BEFORE the build: Tue–Fri builds
 *  drop the day before, a Monday build drops the Friday before. A weekend
 *  build date (only a hand-made event carries one) also drops that Friday.
 *  Same rule in the browser: lib/buildCalendar.ts srsDropDateISO. */
export function srsDropDateISO(buildISO: string): string {
  let d = dayBeforeISO(buildISO);
  while (isWeekendISO(d)) d = dayBeforeISO(d);
  return d;
}

/** "Thursday, October 23" — the format buildCalendarDescription uses. */
export function fmtLong(iso: string | null | undefined): string {
  if (!iso || !DATE_ONLY.test(iso.slice(0, 10))) return "";
  return noon(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

/** "Thu, Oct 23" */
export function fmtShort(iso: string | null | undefined): string {
  if (!iso || !DATE_ONLY.test(iso.slice(0, 10))) return "";
  return noon(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/** "Oct 16" */
export function fmtMonthDay(iso: string | null | undefined): string {
  if (!iso || !DATE_ONLY.test(iso.slice(0, 10))) return "";
  return noon(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── Builds that run more than one day (Oct 26, 2026) ──
// Greg: "sometimes builds are 2 days ... can you add start and end day. its
// always an all day". deals.build_scheduled_date is the first day and
// deals.build_end_date the last (NULL = a one-day build). Neither day falls on
// a weekend, and a build runs at most MAX_BUILD_SPAN_DAYS calendar days.

export const MAX_BUILD_SPAN_DAYS = 14;

/** The last day as stored: null for a one-day build (no end, or an end on or before the start). */
export function normalizeBuildEnd(start: string | null | undefined, end: string | null | undefined): string | null {
  const s = start ? start.slice(0, 10) : "";
  const e = end ? end.slice(0, 10) : "";
  if (!DATE_ONLY.test(s) || !DATE_ONLY.test(e) || e <= s) return null;
  return e;
}

/** Why a last day can't be used, or null when it can (null end = one day). */
export function buildEndProblem(start: string, end: string | null | undefined): string | null {
  if (!end) return null;
  if (!DATE_ONLY.test(end)) return "The last build day must be a date.";
  if (end < start) return "The last day can't be before the first day.";
  if (isWeekendISO(end)) return "No weekend builds. Pick a weekday for the last day.";
  if (end > addDaysISO(start, MAX_BUILD_SPAN_DAYS)) return `A build can run at most ${MAX_BUILD_SPAN_DAYS} days.`;
  return null;
}

/** The working day `n` weekdays after `start` (n = 0 is the start itself). */
export function addWeekdaysISO(start: string, n: number): string {
  let d = start.slice(0, 10);
  for (let i = 0; i < n; i++) {
    d = dayAfterISO(d);
    while (isWeekendISO(d)) d = dayAfterISO(d);
  }
  return d;
}

/** Working days a build covers, first and last included (weekends skipped). */
export function buildDayCount(start: string, end: string | null | undefined): number {
  const last = normalizeBuildEnd(start, end);
  if (!last) return 1;
  let n = 0;
  for (let d = start.slice(0, 10); d <= last; d = dayAfterISO(d)) if (!isWeekendISO(d)) n++;
  return Math.max(1, n);
}

/** "Tuesday, October 27", or "Tuesday, October 27 through Wednesday, October 28". */
export function fmtLongSpan(start: string, end: string | null | undefined, joiner = " through "): string {
  const last = normalizeBuildEnd(start, end);
  return last ? `${fmtLong(start)}${joiner}${fmtLong(last)}` : fmtLong(start);
}

/** "Tue, Oct 27", or "Tue, Oct 27 - Wed, Oct 28" (a plain hyphen keeps texts GSM-7). */
export function fmtShortSpan(start: string | null | undefined, end: string | null | undefined): string {
  if (!start) return "";
  const last = normalizeBuildEnd(start, end);
  return last ? `${fmtShort(start)} - ${fmtShort(last)}` : fmtShort(start);
}

/** The Google Calendar day view for a date, for the "edit it by hand" fallback. */
export function calendarDayUrl(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `https://calendar.google.com/calendar/r/day/${y}/${Number(m)}/${Number(d)}`;
}

// ── Event title ──
// A reschedule prefixes ***DATE CHANGE***; a call-off prefixes ***CALLED OFF***.
// The asterisks match what Robert already types by hand on Team Alex Builds
// ("***date change***27 / Mike Swartz (Bardin) Zavala and Poma***date change***"),
// so the crew and SRS see the marker they know. Either prefix replaces the
// other, a hand-typed marker at either end is recognized, and none is doubled.

const PREFIX_RE = /^\s*\*{0,3}\s*(?:DATE CHANGE|CALLED OFF)\s*\*{0,3}\s*(?:·|-|–|—|:)?\s*/i;
const SUFFIX_RE = /\s*\*{0,3}\s*(?:DATE CHANGE|CALLED OFF)\s*\*{0,3}\s*$/i;

export function stripStatusPrefix(summary: string): string {
  let s = summary ?? "";
  // Loop in case someone hand-typed a second marker.
  for (let i = 0; i < 3 && PREFIX_RE.test(s); i++) s = s.replace(PREFIX_RE, "");
  for (let i = 0; i < 3 && SUFFIX_RE.test(s); i++) s = s.replace(SUFFIX_RE, "");
  return s.trim();
}

export function rescheduledSummary(summary: string): string {
  return `***DATE CHANGE*** ${stripStatusPrefix(summary)}`.trim();
}

export function calledOffSummary(summary: string): string {
  return `***CALLED OFF*** ${stripStatusPrefix(summary)}`.trim();
}

// ── Event description ──
// buildCalendarDescription (PMBoardTab.tsx) writes two lines the crew and SRS
// read: "Drop :    <long date>    <notes>" and "Build :    <long date>". A
// reschedule rewrites those in place and appends a change block. Only the
// reason LABEL goes in: SRS and Risol are guests and can read the description,
// so Robert's internal note never does.

export interface DescriptionChange {
  oldBuildDate: string | null;
  newBuildDate: string | null;   // null = called off, no date yet
  /** Last day of a multi-day build before / after the change (null = one day). */
  oldBuildEndDate?: string | null;
  newBuildEndDate?: string | null;
  newDropDate: string | null;
  dropNotes: string;
  reasonKey: ReasonKey;
  actorName: string;
  changedOn: string;             // date-only, ET
}

// [ \t]* and not \s*: under the m flag \s* would swallow the blank line above
// the Drop/Build line and the layout would lose a line on every rewrite.
const BUILD_LINE = /^([ \t]*Build[ \t]*:[ \t]*).*$/m;
const DROP_LINE = /^([ \t]*Drop[ \t]*:[ \t]*).*$/m;

export function rewriteDescription(description: string | null | undefined, c: DescriptionChange): string {
  let d = description ?? "";
  const label = REASON_LABELS[c.reasonKey] ?? c.reasonKey;

  const oldSpan = c.oldBuildDate ? fmtShortSpan(c.oldBuildDate, c.oldBuildEndDate) : "";
  const newSpan = c.newBuildDate ? fmtShortSpan(c.newBuildDate, c.newBuildEndDate) : "";
  const buildText = c.newBuildDate
    ? `Build :    ${fmtLongSpan(c.newBuildDate, c.newBuildEndDate, " - ")}`
    : `Build :    TBD - called off ${oldSpan}`.trimEnd();
  d = BUILD_LINE.test(d) ? d.replace(BUILD_LINE, buildText) : d;

  if (c.newBuildDate && c.newDropDate) {
    const dropText = `Drop :    ${fmtLong(c.newDropDate)}    ${c.dropNotes || "Early AM"}`;
    d = DROP_LINE.test(d) ? d.replace(DROP_LINE, dropText) : d;
  }

  const lines: string[] = [];
  if (c.newBuildDate) {
    lines.push(`=== DATE CHANGE (${fmtMonthDay(c.changedOn)}) ===`);
    lines.push(
      !c.oldBuildDate
        ? `Build set for ${newSpan} after being called off - ${label}.`
        : c.oldBuildDate === c.newBuildDate
          ? `Build now runs ${newSpan} (was ${oldSpan}) - ${label}.`
          : `Build moved from ${oldSpan} to ${newSpan} - ${label}.`,
    );
    if (c.newDropDate) lines.push(`Drop now ${fmtShort(c.newDropDate)}${c.dropNotes ? ` - ${c.dropNotes}` : ""}.`);
  } else {
    lines.push(`=== CALLED OFF (${fmtMonthDay(c.changedOn)}) ===`);
    lines.push(`Build on ${oldSpan} called off - ${label}. New date to follow.`);
  }
  if (c.actorName) lines.push(c.actorName);

  return `${d.replace(/\s+$/, "")}\n\n${lines.join("\n")}`;
}

// ── Finding a hand-made event ──
// Google's free-text search (q) matches title, description, location and
// guests. The template put the full address in location, so "number + first
// street word" ("1247 Mt") is the tightest key that survives suffix spelling.

export function streetSearchKey(street: string | null | undefined): string {
  const s = (street ?? "").trim().replace(/\s+/g, " ");
  const m = s.match(/^(\d+[A-Za-z]?)\s+(\S+)/);
  if (m) return `${m[1]} ${m[2]}`;
  return s.split(" ").slice(0, 2).join(" ");
}

export interface CandidateEvent {
  id: string;
  summary: string;
  startDate: string | null;      // all-day start.date, else the dateTime's date part
  location: string | null;
  htmlLink: string | null;
}

/** Rank search hits: an all-day event on the old build date whose location
 *  carries the street number wins; then same-date; then location match. */
export function rankCandidates(cands: CandidateEvent[], street: string | null | undefined, oldBuildDate: string | null): CandidateEvent[] {
  const num = ((street ?? "").trim().match(/^\d+/) ?? [""])[0];
  const score = (c: CandidateEvent) => {
    let s = 0;
    if (oldBuildDate && c.startDate === oldBuildDate) s += 2;
    if (num && (c.location ?? "").includes(num)) s += 1;
    return s;
  };
  return [...cands].sort((a, b) => score(b) - score(a));
}

// ── Schedule build (Oct 19, 2026) ──
// Greg: "Chad's approval is just making sure if the first payment has not been
// received, there is a plan. whether they are picking up check before build
// day, build day pick up or online payment by build day. if one of those are
// chosen and that is added to the text to the homeowner, allow and skip chad.
// Chad wants a text notification when that is the case so he can follow up."
//
// The homeowner text is T1 from the verbiage deck (draft v2, Oct 19: leads with
// Peach Duck Contracting) with the payment plan sentence added. Swap the wording
// here when Shannon and Chad's edits come back; the Schedule build window
// previews exactly this text.
//
// Oct 21, 2026, Greg: "move the gate for initial payment from roberts 'schedule
// build' to submitting the build ... verify check by build day, online payment
// by build day or check with mortgagor for endorsement or other where they can
// type in the reason." The plan is now picked when the build is submitted and
// stored in build_payment_plans; Schedule build reads it. Those four are the
// keys offered. The two Oct 19 check keys stay readable, and are still accepted
// from a Schedule build window loaded before the change.

export type PaymentPlanKey =
  | "check_by_build_day" | "online" | "mortgage_endorsement" | "other"
  | "check_before_build" | "check_build_day";

/** The plans offered when a build is submitted. ⚠ The build_payment_plans
 *  CHECK constraint (migration 20261021c) lists the same keys. */
export const PAYMENT_PLAN_KEYS: PaymentPlanKey[] = ["check_by_build_day", "online", "mortgage_endorsement", "other"];

/** Staff-facing labels: the submit form, the Schedule build window, Chad's text, the RM notice. */
export const PAYMENT_PLAN_LABELS: Record<PaymentPlanKey, string> = {
  check_by_build_day: "Check by build day",
  online: "Online payment by build day",
  mortgage_endorsement: "Check with mortgage company for endorsement",
  other: "Other",
  check_before_build: "Check pickup before build day",
  check_build_day: "Check pickup on build day",
};

/** Any plan key the CRM has stored, offered or not. */
export function isPaymentPlanKey(v: unknown): v is PaymentPlanKey {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(PAYMENT_PLAN_LABELS, v);
}

/** "Other" needs the typed reason (3+ characters, as the table's CHECK). */
export function planNeedsNote(plan: PaymentPlanKey | "" | null | undefined): boolean {
  return plan === "other";
}

/** "$8,423.17" */
export function fmtUSD(n: number): string {
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const NAME_PREFIX = /^(?:mr|mrs|ms|miss|dr)\.?\s+/i;

/** The name a text opens with. County-record names come last-name-first in
 *  capitals ("HOFFER GREGORY H"), so a capitalized three-part name ending in
 *  an initial uses the middle word. Anything else uses the first word.
 *  "Last, First" is handled. Capitals become title case. Blank -> "there". */
export function firstNameForText(fullName: string | null | undefined): string {
  let s = (fullName ?? "").trim().replace(/\s+/g, " ");
  s = s.replace(NAME_PREFIX, "");
  if (!s) return "there";
  let first: string;
  if (s.includes(",")) {
    first = ((s.split(",")[1] ?? "").trim().split(" ")[0]) ?? "";
  } else {
    const parts = s.split(" ");
    const allCaps = s === s.toUpperCase() && /[A-Z]/.test(s);
    first = allCaps && parts.length >= 3 && /^[A-Z]\.?$/.test(parts[parts.length - 1]) ? parts[1] : parts[0];
  }
  first = first.replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");
  if (!first) return "there";
  if (first.length > 1 && first === first.toUpperCase()) first = first[0] + first.slice(1).toLowerCase();
  return first;
}

/** The homeowner portal's Payment tab: the same token notifyHomeownerPaymentDue
 *  builds (deal id : last word of the name : street number, base64). */
export function portalPayLink(dealId: string, fullName: string | null | undefined, street: string | null | undefined): string | null {
  const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
  const last = parts[parts.length - 1];
  const num = ((street ?? "").trim().match(/^(\d+)/) ?? [])[1];
  if (!dealId || !last || !num) return null;
  try {
    return `https://portal.bardinroofing.com/status?token=${encodeURIComponent(btoa(`${dealId}:${last}:${num}`))}&tab=payment`;
  } catch {
    return null; // a character btoa can't encode
  }
}

export interface PaymentLine {
  plan: PaymentPlanKey;
  /** Still owed. null = unknown (no scope entered yet). */
  amount: number | null;
  /** "first payment" (insurance) or "50% deposit" (retail). */
  label: string;
  /** Only used by the online plan. */
  payLink: string | null;
}

/** The homeowner's sentence for each plan. "Other" never carries the typed
 *  reason: that note is for the team, not the homeowner. */
export function paymentPlanSentence(p: PaymentLine): string {
  const what = p.amount != null && p.amount > 0 ? `Your ${p.label} of ${fmtUSD(p.amount)}` : `Your ${p.label}`;
  switch (p.plan) {
    case "check_by_build_day":
      return `${what} is due by build day, and we will pick up the check.`;
    case "online":
      return p.payLink
        ? `${what} is due by build day. You can pay it online here: ${p.payLink}`
        : `${what} is due by build day. You can pay it online in your Peach Duck Contracting portal.`;
    case "mortgage_endorsement":
      return `${what} comes from your insurance check, which is with your mortgage company for endorsement. We will collect it as soon as it comes back.`;
    case "other":
      return `${what} is still open, and we will be in touch about it.`;
    case "check_before_build":
      return `${what} is due before build day, and we will come by to pick up the check.`;
    case "check_build_day":
      return `${what} is due on build day, and we will pick up the check then.`;
  }
}

export interface BuildScheduledTextInput {
  firstName: string;
  street: string | null | undefined;
  buildDate: string;
  /** Last day of a multi-day build (null or missing = one day). */
  buildEndDate?: string | null;
  /** null when the first payment is already in. */
  payment: PaymentLine | null;
}

/** T1 "build scheduled" (verbiage deck draft v2, Oct 19, 2026: leads with Peach Duck Contracting,
 *  with "(Bardin Roofing)", the name the homeowner already knows) plus the payment plan sentence. */
export function buildScheduledText(i: BuildScheduledTextInput): string {
  const street = (i.street ?? "").trim();
  const multiDay = normalizeBuildEnd(i.buildDate, i.buildEndDate) !== null;
  const parts = [
    `Hi ${i.firstName}, it's Peach Duck Contracting (Bardin Roofing).`,
    `Your roof build${street ? ` at ${street}` : ""} is scheduled for ${fmtLongSpan(i.buildDate, i.buildEndDate)}.`,
    multiDay
      ? "Materials usually arrive the day before and the crew starts about 6:30 am each day, so please have cars out of the driveway those mornings."
      : "Materials usually arrive the day before and the crew starts about 6:30 am, so please have cars out of the driveway that morning.",
  ];
  if (i.payment) parts.push(paymentPlanSentence(i.payment));
  parts.push("Here's what to expect: portal.bardinroofing.com/build-day.");
  parts.push("Reply here with any questions. Reply STOP to opt out.");
  return parts.join(" ");
}

export interface PaymentFollowupTextInput {
  firstName: string;
  buildDate: string;
  buildEndDate?: string | null;
  payment: PaymentLine;
}

/** The automatic build text (homeowner-comms) already told the homeowner the
 *  date, so the Schedule build button sends only the first payment line. */
export function buildPaymentFollowupText(i: PaymentFollowupTextInput): string {
  return [
    `Hi ${i.firstName}, it's Peach Duck Contracting, with one more note about your roof build on ${fmtLongSpan(i.buildDate, i.buildEndDate)}.`,
    paymentPlanSentence(i.payment),
    "Reply here with any questions.",
  ].join(" ");
}

/** Which text the Schedule build button sends. `engineToldAt` is when the
 *  automatic build text told the homeowner this same date (null if it didn't). */
export function scheduleTextKind(engineToldAt: string | null, hasPayment: boolean): "full" | "payment_only" | "already_texted" {
  if (!engineToldAt) return "full";
  return hasPayment ? "payment_only" : "already_texted";
}

export interface ChadPlanTextInput {
  homeowner: string;
  street: string | null | undefined;
  buildDate: string;
  buildEndDate?: string | null;
  plan: PaymentPlanKey;
  /** The submitter's note (required for "other"). */
  note?: string | null;
  amount: number | null;
  label: string;
  /** Scheduled while the deal was still at install_meeting or build_submitted. */
  beforeApproval: boolean;
  actorName: string;
  dealUrl: string;
}

/** "Plan: other (son pays on build day)". The note is trimmed to keep the text short. */
export function planPhrase(plan: PaymentPlanKey, note?: string | null): string {
  const n = (note ?? "").replace(/\s+/g, " ").trim();
  const short = n.length > 120 ? `${n.slice(0, 117)}...` : n;
  return `${PAYMENT_PLAN_LABELS[plan].toLowerCase()}${short ? ` (${short})` : ""}`;
}

/** Chad's Team-line text when a build is scheduled with the first payment still out. */
export function chadPlanText(i: ChadPlanTextInput): string {
  const head = i.beforeApproval ? "Build scheduled before your approval" : "Build scheduled";
  const street = (i.street ?? "").trim();
  const where = street ? `${i.homeowner}, ${street}` : i.homeowner;
  const label = i.label.charAt(0).toUpperCase() + i.label.slice(1);
  const money = i.amount != null && i.amount > 0 ? `${label} of ${fmtUSD(i.amount)} not in yet` : `${label} not in yet (amount unknown)`;
  return `${head}: ${where}. Build ${fmtShortSpan(i.buildDate, i.buildEndDate)}. ${money}. Plan: ${planPhrase(i.plan, i.note)}. Scheduled by ${i.actorName}. ${i.dealUrl}`;
}

/** Homeowner texts go out 8 AM to 9 PM Eastern. A build today or tomorrow can't
 *  wait for the morning, so it goes now whatever the hour. */
export function homeownerTextTiming(etHour: number, etToday: string, buildDate: string): "now" | "morning" {
  if (etHour >= 8 && etHour < 21) return "now";
  if (buildDate <= addDaysISO(etToday, 1)) return "now";
  return "morning";
}

// GSM-7: the basic set counts 1, the extension set 2. Anything else (an em
// dash, curly quotes) turns the whole text into UCS-2 and shrinks a segment
// from 153 to 67 characters.
const GSM7_BASIC = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXT = "^{}\\[~]|€\f";

export function smsInfo(text: string): { gsm7: boolean; chars: number; segments: number } {
  let units = 0;
  for (const ch of text) {
    if (GSM7_BASIC.includes(ch)) units += 1;
    else if (GSM7_EXT.includes(ch)) units += 2;
    else {
      const u16 = text.length;
      return { gsm7: false, chars: u16, segments: u16 <= 70 ? 1 : Math.ceil(u16 / 67) };
    }
  }
  return { gsm7: true, chars: units, segments: units <= 160 ? 1 : Math.ceil(units / 153) };
}

/** The calendar event's Special Instructions: the PM sheet list first, then
 *  every line of the deal's build notes the list doesn't already say (Greg,
 *  Oct 19, 2026: the build notes never reached the event). */
export function mergeSpecialInstructions(list: Array<string | null | undefined> | null | undefined, buildNotes: string | null | undefined): string[] {
  const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").replace(/[\s.,;:!-]+$/, "").trim();
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (raw: string | null | undefined) => {
    const line = (raw ?? "").trim();
    if (!line) return;
    const key = norm(line);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(line);
  };
  (list ?? []).forEach(add);
  (buildNotes ?? "").split(/\r?\n/).forEach(add);
  return out;
}
